/**
 * Staff Dashboard - Unified View for Case Management
 * =====================================================
 * 
 * Dedicated dashboard for staff (accountants, data specialists) combining:
 * - Queue management (claim/release)
 * - Case readiness status
 * - Extracted document summaries
 * - Sign-off workflow
 * 
 * P3: Dashboard Integration
 * @author ClaimSync Team
 * @created August 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '../contexts/TestAuthContext';
import AppLayout from '../components/AppLayout';
import { api } from '../utils/api';
import { FinancialValue } from '../components/shared/FinancialValue';
import { NOT_COMPUTED_TEXT, ZERO_TEXT } from '../utils/financialFormat';
import { notComputedReason, type NotComputedMap } from '../services/notComputed';
import CaseReadinessPanel from '../components/CaseReadinessPanel';
import ExtractedDocumentsPanel from '../components/ExtractedDocumentsPanel';
import NowLineHeader, { NowLineStats } from '../components/NowLineHeader';
import {
  Users,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  ChevronRight,
  Briefcase,
  Building2,
  DollarSign,
  Search,
  Filter,
  Eye,
  Edit,
  Send,
  X,
  Check
} from 'lucide-react';

interface QueueCase {
  id: string;
  business_name: string;
  client_name: string;
  law_firm?: string;
  status: string;
  pipeline_stage?: string;
  readiness_score?: number;
  is_ready_for_review?: boolean;
  claimed_by_id?: string;
  claimed_by_name?: string;
  claimed_at?: string;
  signed_off?: boolean;
  created_at: string;
  updated_at?: string;
  estimated_value?: number;
}

interface QueueStats {
  available_count: number;
  my_claims_count: number;
  ready_count: number;
  // WP14: null when no claimed case has a canonical net (see not_computed); a real 0 stays 0.
  total_portfolio_value: number | null;
  not_computed?: NotComputedMap;
}

export default function StaffDashboard() {
  const navigate = useNavigate();
  const { userId, getToken } = useAuth();
  const { user } = useUser();
  const currentAccountantId = userId || (user as any)?.id || 'unknown';
  const currentAccountantName = (user as any)?.fullName || (user as any)?.primaryEmailAddress?.emailAddress || 'Staff User';

  // State
  const [activeTab, setActiveTab] = useState<'available' | 'my-cases' | 'ready'>('available');
  const [cases, setCases] = useState<QueueCase[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState<QueueCase | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Now-Line stats for Phase A UX
  const [nowLineStats, setNowLineStats] = useState<NowLineStats>({
    needsAttention: { count: 0, breakdown: [] },
    noActionNeeded: { count: 0, breakdown: [] }
  });
  const [nowLineLoading, setNowLineLoading] = useState(true);

  // Fetch Now-Line stats
  const fetchNowLineStats = async () => {
    setNowLineLoading(true);
    try {
      const token = await getToken();
      const data = await api.get<{needs_attention: any; no_action_needed: any}>(
        '/api/v1/queue/nowline-stats?role=data_specialist',
        { headers: { Authorization: `Bearer ${token}` }, showErrorToast: false }
      );
      setNowLineStats({
        needsAttention: {
          count: data.needs_attention?.count || 0,
          breakdown: data.needs_attention?.breakdown || []
        },
        noActionNeeded: {
          count: data.no_action_needed?.count || 0,
          breakdown: data.no_action_needed?.breakdown || []
        }
      });
    } catch (error) {
      console.debug('Now-Line stats not available:', error);
    } finally {
      setNowLineLoading(false);
    }
  };

  // Sequence guard: ignore stale in-flight responses when tab switches rapidly.
  const tabSeq = useRef(0);

  // Fetch queue data
  const fetchData = async () => {
    const seq = ++tabSeq.current;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats + cases in parallel
      let casesEndpoint = '/api/v1/queue/accountant/available';
      if (activeTab === 'my-cases') {
        casesEndpoint = '/api/v1/queue/accountant/my-cases';
      } else if (activeTab === 'ready') {
        casesEndpoint = '/api/v1/queue/accountant/available?ready_only=true';
      }

      const [statsResult, casesResult] = await Promise.allSettled([
        api.get('/api/v1/queue/accountant/stats', { headers, showErrorToast: false }),
        api.get(casesEndpoint, { headers, showErrorToast: false }),
      ]);

      if (seq !== tabSeq.current) return; // stale

      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value);
      } else {
        console.error('Error fetching stats:', statsResult.reason);
      }

      if (casesResult.status === 'fulfilled') {
        setCases(casesResult.value.cases || []);
      } else {
        console.error('Error fetching cases:', casesResult.reason);
        setCases([]);
      }
    } catch (err: any) {
      if (seq !== tabSeq.current) return; // stale
      console.error('Error fetching queue data:', err);
      setError(err.message || 'Failed to load queue data');
    } finally {
      if (seq === tabSeq.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
    fetchNowLineStats();
  }, [activeTab]);

  // Claim a case
  const handleClaim = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      const token = await getToken();
      // Accountant identity derived from JWT server-side; no query params.
      await api.post(
        `/api/v1/queue/accountant/claim/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to claim case');
    } finally {
      setActionLoading(null);
    }
  };

  // Release a case
  const handleRelease = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      const token = await getToken();
      // Accountant identity derived from JWT server-side; no query params.
      await api.post(
        `/api/v1/queue/accountant/release/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to release case');
    } finally {
      setActionLoading(null);
    }
  };

  // Sign off a case
  const handleSignOff = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      const token = await getToken();
      // Accountant identity derived from JWT server-side; no query params.
      const response = await api.post(
        `/api/v1/case-signoff/${caseId}`,
        { notes: '', override_warnings: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.success) {
        await fetchData();
      } else {
        setError(response.message || 'Sign-off blocked');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign off case');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter cases by search
  const filteredCases = cases.filter(c => {
    const search = searchQuery.toLowerCase();
    return (
      c.business_name?.toLowerCase().includes(search) ||
      c.client_name?.toLowerCase().includes(search) ||
      c.law_firm?.toLowerCase().includes(search) ||
      c.id?.toLowerCase().includes(search)
    );
  });

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="cs-display">Staff Dashboard</h1>
            <p className="cs-meta">Welcome back, {currentAccountantName}</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg cs-body transition-colors"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div
            className="mb-6 p-4 rounded-lg flex items-center justify-between"
            style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
          >
            <div className="flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
              <AlertTriangle className="w-5 h-5" />
              <span className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>{error}</span>
            </div>
            <button onClick={() => setError(null)} style={{ color: 'var(--color-needs-human-text)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Now-Line Header - Phase A UX Enhancement */}
        <NowLineHeader
          stats={nowLineStats}
          userName={currentAccountantName}
          loading={nowLineLoading}
          onViewNeedsAttention={() => setActiveTab('available')}
          onViewNoAction={() => setActiveTab('my-cases')}
        />

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setActiveTab('available')}
              className="rounded-xl p-5 text-left transition-all"
              style={{
                background: 'var(--color-bg-surface)',
                border: `1px solid ${activeTab === 'available' ? 'var(--color-brand)' : 'var(--color-border)'}`,
                boxShadow: activeTab === 'available' ? '0 0 0 2px var(--color-needs-human-tint)' : undefined,
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <Briefcase className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                </div>
              </div>
              <p className="cs-figure-lg">{stats.available_count}</p>
              <p className="cs-meta">Available Cases</p>
            </button>

            <button
              onClick={() => setActiveTab('my-cases')}
              className="rounded-xl p-5 text-left transition-all"
              style={{
                background: 'var(--color-bg-surface)',
                border: `1px solid ${activeTab === 'my-cases' ? 'var(--color-brand)' : 'var(--color-border)'}`,
                boxShadow: activeTab === 'my-cases' ? '0 0 0 2px var(--color-needs-human-tint)' : undefined,
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                  <Users className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                </div>
              </div>
              <p className="cs-figure-lg">{stats.my_claims_count}</p>
              <p className="cs-meta">My Cases</p>
            </button>

            <button
              onClick={() => setActiveTab('ready')}
              className="rounded-xl p-5 text-left transition-all"
              style={{
                background: 'var(--color-bg-surface)',
                border: `1px solid ${activeTab === 'ready' ? 'var(--color-brand)' : 'var(--color-border)'}`,
                boxShadow: activeTab === 'ready' ? '0 0 0 2px var(--color-needs-human-tint)' : undefined,
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'transparent' }}>
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                </div>
              </div>
              <p className="cs-figure-lg">{stats.ready_count}</p>
              <p className="cs-meta">Ready for Attorney</p>
            </button>

            <div className="rounded-xl p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                  <DollarSign className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                </div>
              </div>
              <p className="cs-figure-lg"><FinancialValue value={stats.total_portfolio_value} notRunLabel={NOT_COMPUTED_TEXT} zeroLabel={ZERO_TEXT} reason={notComputedReason(stats.not_computed, 'total_portfolio_value')} /></p>
              <p className="cs-meta">Portfolio Value</p>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cases List (2/3 width) */}
          <div className="lg:col-span-2">
            <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              {/* Search */}
              <div className="p-4" style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                  <input
                    type="text"
                    placeholder="Search cases..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg cs-body"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  />
                </div>
              </div>

              {/* Tab Header */}
              <div className="px-4 py-3" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-sub)' }}>
                <h2 className="cs-h3">
                  {activeTab === 'available' && 'Available Cases'}
                  {activeTab === 'my-cases' && 'My Claimed Cases'}
                  {activeTab === 'ready' && 'Ready for Attorney'}
                  {!loading && <span className="ml-2 cs-meta">({filteredCases.length})</span>}
                </h2>
              </div>

              {/* Cases List */}
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: 'var(--color-brand)' }} />
                  <p className="cs-meta">Loading cases...</p>
                </div>
              ) : filteredCases.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--color-text-ghost)' }} />
                  <p className="cs-meta">
                    {activeTab === 'available' && 'No available cases'}
                    {activeTab === 'my-cases' && 'You have no claimed cases'}
                    {activeTab === 'ready' && 'No cases ready for attorney'}
                  </p>
                </div>
              ) : (
                <div style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                  {filteredCases.map((c) => (
                    <div
                      key={c.id}
                      className="p-4 cursor-pointer transition-colors"
                      style={{
                        borderBottom: '1px solid var(--color-border-faint)',
                        background: selectedCase?.id === c.id ? 'var(--color-needs-human-tint)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (selectedCase?.id !== c.id) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = selectedCase?.id === c.id ? 'var(--color-needs-human-tint)' : 'transparent'; }}
                      onClick={() => setSelectedCase(c)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="cs-body truncate" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                              {c.business_name || c.client_name || 'Unnamed Case'}
                            </h3>
                            {c.is_ready_for_review && (
                              <span className="px-2 py-0.5 cs-chip rounded" style={{ background: 'transparent', color: 'var(--color-verified)' }}>
                                Ready
                              </span>
                            )}
                            {c.signed_off && (
                              <span className="px-2 py-0.5 cs-chip rounded" style={{ background: 'transparent', color: 'var(--color-verified)' }}>
                                Signed Off
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 cs-meta">
                            {c.law_firm && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {c.law_firm}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              <FinancialValue value={c.estimated_value} />
                            </span>
                            {c.claimed_by_name && activeTab !== 'my-cases' && (
                              <span style={{ color: 'var(--color-needs-human-text)' }}>
                                Claimed by {c.claimed_by_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Action Buttons */}
                          {activeTab === 'available' && !c.claimed_by_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleClaim(c.id); }}
                              disabled={actionLoading === c.id}
                              className="px-3 py-1.5 cs-body rounded-lg disabled:opacity-50 transition-colors"
                              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                            >
                              {actionLoading === c.id ? 'Claiming...' : 'Claim'}
                            </button>
                          )}
                          {activeTab === 'my-cases' && (
                            <>
                              {c.is_ready_for_review && !c.signed_off && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSignOff(c.id); }}
                                  disabled={actionLoading === c.id}
                                  className="px-3 py-1.5 cs-body rounded-lg disabled:opacity-50"
                                  style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)', fontWeight: 500 }}
                                >
                                  {actionLoading === c.id ? 'Signing...' : 'Sign Off'}
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRelease(c.id); }}
                                disabled={actionLoading === c.id}
                                className="px-3 py-1.5 cs-body rounded-lg disabled:opacity-50"
                                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
                              >
                                Release
                              </button>
                            </>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/report/${c.id}`); }}
                            className="p-1.5"
                            style={{ color: 'var(--color-text-dim)' }}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Side Panel (1/3 width) */}
          <div className="space-y-6">
            {selectedCase ? (
              <>
                {/* Selected Case Info */}
                <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                  <h3 className="cs-h3 mb-2">
                    {selectedCase.business_name || selectedCase.client_name}
                  </h3>
                  <p className="cs-meta mb-4">ID: {selectedCase.id}</p>
                  <button
                    onClick={() => navigate(`/report/${selectedCase.id}`)}
                    className="w-full px-4 py-2 rounded-lg cs-body flex items-center justify-center gap-2 transition-colors"
                    style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                  >
                    <Eye className="w-4 h-4" />
                    View Full Report
                  </button>
                </div>

                {/* Readiness Panel */}
                <CaseReadinessPanel
                  caseId={selectedCase.id}
                  compact={false}
                  showRefresh={true}
                />

                {/* Extracted Documents */}
                <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                  <ExtractedDocumentsPanel caseId={selectedCase.id} compact={true} />
                </div>
              </>
            ) : (
              <div className="rounded-xl p-8 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-ghost)' }} />
                <p className="cs-meta">Select a case to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
